/*
段階ヒントの最後の段「答え」を出す章と手順を決める。板 (progress.ts の hintLadder) と、書き出し (export-cloudeditor-guides.ts が
api-go の表 lesson_guides_gen.go に載せる) が同じ関数を使う。章のデータには書かない (手で書くと食い違う)。

答えを出さない章は、道場 (確認テストの代わり)、宿題 (提出が答えの代わり)、問いの手順の章、模範解答の無い手書きの章。
元のレッスンを写した章 (lessonId を持つ章) だけが、api-go が元のレッスンの模範解答を返せる。
*/
import { findGuideEntry } from "./guide-index";
import type { AnswerPolicy, Guide, GuideCheck, GuideStep } from "./guides";

/** 機械で確かめる条件。受講生の端末の道具 (CLI) が上から見る並びと同じ (手動、提出、自己申告は数えない)。落ちた条件の番号 (verdict.failed) はこの並びの位置 */
export type MachineCheck = Exclude<GuideCheck, { type: "manual" | "handed-in" | "attest" }>;

export function machineChecksOf(step: Pick<GuideStep, "check">): MachineCheck[] {
  const list = step.check === undefined ? [] : Array.isArray(step.check) ? step.check : [step.check];
  return list.filter((c): c is MachineCheck => c.type !== "manual" && c.type !== "handed-in" && c.type !== "attest");
}

/** 手順のヒントの段の数 (hint と hints)。progress.ts の hintTiers の数と同じ */
function tierCount(step: Pick<GuideStep, "hint" | "hints">): number {
  return [step.hint, ...(step.hints ?? [])].filter(Boolean).length;
}

/** 落ちた条件で強く押す段の数。条件ごとの hints があればそれ、無ければ手順のヒント */
export function pushCount(check: MachineCheck | undefined, step: Pick<GuideStep, "hint" | "hints">): number {
  return check?.hints?.length ? check.hints.length : tierCount(step);
}

/** 答えを出す章か。道場と宿題と問いの手順の章は、元のレッスンを持っていても出さない */
export function answerPolicyOf(guide: Pick<Guide, "id" | "lessonId" | "steps">): AnswerPolicy {
  if (guide.id.startsWith("dojo-") || findGuideEntry(guide.id)?.dojo) return "never";
  if (guide.steps.some((step) => step.handIn || step.quiz)) return "never";
  return guide.lessonId ? "after-hints" : "never";
}

/** 答えを出す手順。元のレッスンの解答に当たる手順 (写した章の solve) だけ */
export const ANSWER_STEP_ID = "solve";

/** 同じ人が 1 つの手順で開けるヒントの段の上限 (api-go の max_hint_level の上限と同じ) */
export const MAX_HINT_LEVEL = 9;

/**
 * 手順の答えを開くのに、ヒントを開いておく段の数。答えを出さない手順は null。
 * 落ちたときの段は「軽く押す」1 段と強く押す段 (条件ごと) なので、どの条件で落ちても全部開けば届く最小の数 (1 + 最も少ない強く押す段) にする。
 * api-go は、板が記録した開いた段の数がこの数に届いていることを答えの条件の 1 つにする
 */
export function answerHintLevel(guide: Pick<Guide, "id" | "lessonId" | "steps">, step: GuideStep): number | null {
  if (step.id !== ANSWER_STEP_ID || answerPolicyOf(guide) !== "after-hints") return null;
  const checks = machineChecksOf(step);
  if (checks.length === 0) return null;
  return Math.min(MAX_HINT_LEVEL, 1 + Math.min(...checks.map((c) => pushCount(c, step))));
}
